import React, { useEffect } from "react";
import AppConfig from "../consts/AppConfig";
import styles from "./Menu.module.css";
import MenuSection from "./MenuSection";
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ColorPalette from "../colorpalette/ColorPalette";
import LogoFull from "../assets/images/logo-full.png";

/**
 * Mobile menu component that displays navigation options
 * Implements accessibility features and smooth transitions
 */
const Menu = ({ OnHide, disableNavigation = false, allowedRoutes = [] }) => {
    useEffect(() => {
        // Prevent scrolling of the background content when menu is open
        const body = document.querySelector('body');
        const html = document.documentElement;

        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';

        // Add event listener for escape key to close menu
        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                OnHide();
            }
        };
        document.addEventListener('keydown', handleEscKey);

        return () => {
            // Restore scrolling when menu is closed
            body.style.overflow = 'auto';
            html.style.overflow = 'auto';
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [OnHide]);

    const handleCloseClick = () => {
        OnHide();
    };

    // Check whether the current user is a platform admin (from session data in localStorage)
    const isPlatformAdmin = () => {
        try {
            const sessionData = localStorage.getItem('sessionData');
            if (!sessionData) return false;
            const session = JSON.parse(sessionData);
            return Boolean(session?.user?.isPlatformAdmin);
        } catch (error) {
            return false;
        }
    };

    // Check whether the current user owns the current company (from session data in localStorage)
    const isCompanyOwner = () => {
        try {
            const session = JSON.parse(localStorage.getItem('sessionData') || 'null');
            const role = session?.companies?.find(company => company?.id == session?.currentCompany)?.role?.name;
            return role === 'COMPANY_OWNER';
        } catch (error) {
            return false;
        }
    };

    // Get menu items for each section, filtering out platform-admin-only routes for non platform admins
    const getSectionItems = (routes) => {
        return routes
            .map(routeId => AppConfig.GetRouteById(routeId))
            .filter(route => {
                if (route && route.platformAdminOnly && !isPlatformAdmin()) {
                    return false;
                }
                if (route && route.ownerOnly && !isCompanyOwner()) {
                    return false;
                }
                return Boolean(route);
            });
    };

    // Filter out sections that have no visible items after filtering
    const visibleMenuSections = AppConfig.MenuRoutes.filter(section => getSectionItems(section.routes).length > 0);

    return (
        <nav
            className={styles.container}
            role="navigation"
            aria-label="Mobile menu"
        >
            <div className={styles.header}>
                <img src={LogoFull} className={styles.logo} alt="Gabriela" />
                <button
                    className={styles.close}
                    onClick={handleCloseClick}
                    aria-label="Fechar menu"
                >
                    <FontAwesomeIcon
                        icon={faClose}
                        style={{ color: ColorPalette.textMedium }}
                    />
                </button>
            </div>
            <div className={styles.list}>
                {visibleMenuSections.map((section, index) => (
                    <MenuSection
                        key={index}
                        title={section.label}
                        icon={section.icon}
                        items={getSectionItems(section.routes)}
                        OnHide={OnHide}
                        disableNavigation={disableNavigation}
                        allowedRoutes={allowedRoutes}
                    />
                ))}
            </div>
        </nav>
    );
}

export default Menu;